import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogPortal, DialogOverlay } from '@/components/ui/dialog';
import { Form } from 'react-admin';
import { useRecordContext, useTranslate } from 'ra-core';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import LastNegotiation from '../negotiation/LastNegotiation';
import { ReactNode } from 'react';
import { Offer } from '../types';

const lastPointer = { x: -1, y: -1 };
if (typeof window !== 'undefined') {
  window.addEventListener(
    'pointerdown',
    e => {
      lastPointer.x = e.clientX;
      lastPointer.y = e.clientY;
    },
    { capture: true, passive: true }
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  actions: ReactNode;
  onSubmit: ((formData: any) => void) | undefined;
};

function BaseNegotiationModal({
  open,
  onClose,
  title,
  children,
  actions,
  onSubmit,
}: Props) {
  const t = useTranslate();
  const offer = useRecordContext<Offer>();
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    if (!el || lastPointer.x < 0) return;
    const rect = el.getBoundingClientRect();
    const clamp = (n: number) => Math.max(0, Math.min(100, n));
    const ox = clamp(((lastPointer.x - rect.left) / rect.width) * 100);
    const oy = clamp(((lastPointer.y - rect.top) / rect.height) * 100);
    el.style.transformOrigin = `${ox}% ${oy}%`;
  }, [open]);

  if (!offer) return null;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={contentRef}
          data-slot="dialog-content"
          className="bg-card border border-border rounded-2xl overflow-hidden fixed top-[50%] left-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-[calc(100%-2rem)] max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col shadow-elev-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 duration-500"
        >
          {/* Fixed header */}
          <div className="shrink-0 border-b border-border px-5 py-4 flex items-center justify-between">
            <DialogPrimitive.Title className="text-base font-semibold leading-5 -tracking-[0.2px] text-foreground">
              {title}
            </DialogPrimitive.Title>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 rounded-md text-muted-foreground hover:text-foreground"
              onClick={onClose}
            >
              <X className="size-4" strokeWidth={2.5} />
            </Button>
          </div>

          {/* Scrollable area: last negotiation + form + footer all scroll together */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="px-6 py-4">
              <LastNegotiation />
            </div>
            <hr className="border-border" />

            <Form record={offer.last_negotiation} onSubmit={onSubmit}>
              <div className="px-6 py-4">{children}</div>

              {/* Footer at the bottom of the form */}
              <div className="border-t border-border bg-card px-5 py-4 flex items-center justify-between shadow-[0_-10px_24px_-16px_rgba(20,23,43,0.18)]">
                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  className="text-muted-foreground"
                  onClick={onClose}
                >
                  {t('ra.action.close')}
                </Button>
                <div className="flex items-center gap-2">{actions}</div>
              </div>
            </Form>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default BaseNegotiationModal;
